import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useAccount, useBillingSummary, useCurrentUser } from '@/auth/useSession';
import { SampleBanner } from '@/features/sample/SampleBanner';
import { formatUtcDateTime } from '@/lib/format';
import { SCREENS } from './screens';

const badge = 'rounded-[4px] px-1.5 py-px text-2xs font-semibold tracking-wide';

/** Past-due, support-session, demo and sample-data banners (parity §1), above every screen. */
export function Banners({ onExitSupport }: { onExitSupport: () => void }) {
  const billing = useBillingSummary();
  const me = useCurrentUser();
  const account = useAccount();
  const support = me.data?.support_session ?? null;
  const pastDue = billing.data?.processing.delinquent === true;
  const canManage = billing.data?.permissions.can_manage_billing === true;

  return (
    <>
      {pastDue ? (
        <div role="alert" className="sticky top-0 z-30 flex flex-wrap items-center gap-2.5 border-b border-line bg-warn-wash px-gutter py-2 text-sm text-warn-ink">
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          <span>Payment is past due.</span>
          <Link to={SCREENS.billing.path} className="font-semibold underline-offset-2 hover:underline">
            {canManage ? 'Fix billing' : 'View billing'}
          </Link>
        </div>
      ) : null}
      {support ? (
        <div role="status" className="flex flex-wrap items-center gap-2.5 border-b border-line bg-accent-wash px-gutter py-2 text-sm text-fg">
          <span className={`${badge} bg-accent-ink text-on-blue`}>SUPPORT</span>
          <span>
            Viewing as <strong className="font-semibold">{me.data?.email_address}</strong> · read-only · ends{' '}
            {formatUtcDateTime(support.expires_at)}
          </span>
          <button type="button" onClick={onExitSupport} className="ml-auto border-0 bg-transparent p-0 font-semibold text-accent-ink hover:underline">
            Exit support session
          </button>
        </div>
      ) : null}
      {account.data?.is_demo ? (
        <div role="status" className="flex flex-wrap items-center gap-2.5 border-b border-line bg-head px-gutter py-2 text-sm text-muted">
          <span className={`${badge} bg-brand text-on-brand`}>DEMO</span>
          <span>Explore freely — live billing, connections, ad-network uploads, and notifications are disabled.</span>
        </div>
      ) : null}
      <SampleBanner />
    </>
  );
}
